import { useMemo } from 'react';

import type { ColGroupDef } from 'ag-grid-enterprise';

import type { ListCompanyBilling } from '@enterprise/api';

import type { ListSegmentCellProps } from '@components/grid/renderers/types/list-segment-renderer.params';

import DateCellRenderer from '@components/grid/renderers/date-cell.renderer';
import ListSegmentRenderer from '@components/grid/renderers/list-segment-cell-renderer';
import NumberCellRenderer from '@components/grid/renderers/number-cell.renderer';

export const useGetListCompanyBillingColumns = () => {
  return useMemo(() => {
    return [
      {
        children: [
          {
            field: 'mailingId',
            headerName: 'Mailing ID',
            hide: true,
            type: ['numericColumn'],
            width: 175,
          },
          {
            cellDataType: 'date',
            cellRenderer: DateCellRenderer,
            field: 'mailDate',
            headerName: 'Mail Date',
            width: 175,
          },
          {
            field: 'mailCode',
            headerName: 'Mail Code',
            width: 150,
          },
          {
            cellRenderer: ListSegmentRenderer,
            cellRendererParams: (params: ListSegmentCellProps<ListCompanyBilling>) => {
              return {
                ...params,
                header: 'listDisplayName',
                subHeader: 'listOwner',
              } as ListSegmentCellProps<ListCompanyBilling>;
            },
            field: 'listDisplayName',
            headerName: 'List/Segment',
            wrapText: true,
          },
          {
            field: 'listOwner',
            headerName: 'List Owner',
            hide: true,
            wrapText: true,
          },
          {
            field: 'purchaseOrderNumber',
            headerName: 'Order #',
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellRenderer: NumberCellRenderer,
            field: 'quantityOrdered',
            headerName: 'Requested',
            type: ['numericColumn'],
            width: 145,
          },
          {
            aggFunc: 'sum',
            cellRenderer: NumberCellRenderer,
            cellStyle: { fontWeight: 'bold' },
            field: 'quantityReceived',
            headerName: 'Received',
            type: ['numericColumn'],
            width: 145,
          },
        ],
        headerClass: ['bg-blue-950'],
        headerName: 'Mailing and List Information',
      },
      {
        children: [
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantitySuppressed',
            headerClass: ['bg-yellow-800'],
            headerName: 'Suppress',
            type: ['numericColumn'],
            width: 175,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantityDupes',
            headerClass: ['bg-yellow-800'],
            headerName: 'Dupes',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantitySameListDupes',
            headerClass: ['bg-yellow-800'],
            headerName: 'Intra List',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantityStateOmits',
            headerClass: ['bg-yellow-800'],
            headerName: 'State Omits',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantityBadRecords',
            headerClass: ['bg-yellow-800'],
            headerName: 'Bad Recs',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantityDeceased',
            headerClass: ['bg-yellow-800'],
            headerName: 'Deceased',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantityFinalOutput',
            filter: 'agNumberColumnFilter',
            headerClass: ['bg-yellow-800'],
            headerName: 'Final Output',
            type: ['numericColumn'],
            width: 175,
          },
        ],
        headerClass: ['bg-yellow-900'],
        headerGroupComponentParams: {
          icon: 'fa-user-minus',
        },
        headerName: 'Suppression Categories',
      },
    ] as ColGroupDef<ListCompanyBilling>[];
  }, []);
};
